/**
 * Utilities for printing payment receipts
 */

/**
 * Generates HTML content for printing a payment receipt
 * @param {Object} payment - Payment data
 * @param {Object} patient - Patient data
 * @param {string} logoUrl - Organization logo URL (optional)
 * @param {Object} organization - Organization data (optional)
 * @param {Object} doctor - Doctor/profile data (optional)
 * @param {Array} paymentForms - List of payment forms (optional)
 * @param {Array} users - List of users for allocation label (optional)
 * @returns {string} HTML content for printing
 */
export const generatePaymentReceiptHTML = (
  payment,
  patient,
  logoUrl = null,
  organization = null,
  doctor = null,
  paymentForms = [],
  users = []
) => {
  const formatClinicAddress = (address) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    if (typeof address === 'object') {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zipCode) parts.push(address.zipCode);
      if (address.country) parts.push(address.country);
      return parts.join(', ');
    }
    return '';
  };

  const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Paciente';
  const timezone = organization?.timezone || 'America/Mexico_City';
  const patientDob = patient?.dateOfBirth
    ? new Date(patient.dateOfBirth).toLocaleDateString('es-ES', { timeZone: timezone })
    : null;
  const clinicName = organization?.name || doctor?.institution || 'Clínica Dental';
  const clinicAddress = formatClinicAddress(organization?.address);
  const clinicPhone = organization?.phone || '';

  const doctorName = doctor?.name || doctor?.firstName
    ? `${doctor?.firstName || ''} ${doctor?.lastName || ''}`.trim()
    : null;
  const doctorTitle = doctor?.specialization || doctor?.title || 'Cirujano Dentista';
  const doctorLicense = doctor?.license || doctor?.professionalLicense || null;

  const currentDate = new Date().toLocaleDateString('es-ES', { timeZone: timezone, day: '2-digit', month: '2-digit', year: 'numeric' });
  const paymentDate = payment.paymentDate || payment.createdAt
    ? new Date(payment.paymentDate || payment.createdAt).toLocaleDateString('es-ES', { timeZone: timezone, day: '2-digit', month: '2-digit', year: 'numeric' })
    : currentDate;

  const paymentFormName = paymentForms.find(f => f.id === payment.paymentFormId)?.name || 'No especificada';

  const getAllocationLabel = () => {
    if (!payment.allocationUserId) return null;
    if (payment.allocationUserId === 'GENERAL_SALES') return 'Venta General';
    const user = users.find(u => u.id === payment.allocationUserId);
    return user ? (user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()) : 'Usuario desconocido';
  };

  const allocationLabel = getAllocationLabel();

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(amount || 0));

  const paymentAmount = Number(payment.amount || 0);
  const refundAmount = Number(payment.refundAmount || 0);
  const netAmount = paymentAmount - refundAmount;
  const isFullyRefunded = refundAmount >= paymentAmount && refundAmount > 0;
  const hasRefunds = refundAmount > 0;
  const paymentConcept = payment.concept?.trim() || 'Pago recibido';

  const refundsHistoryRows = () => {
    if (!payment.refunds || payment.refunds.length === 0) {
      if (payment.refundNote && hasRefunds) {
        const refundDate = payment.refundDate
          ? new Date(payment.refundDate).toLocaleDateString('es-ES', { timeZone: timezone, day: '2-digit', month: '2-digit', year: 'numeric' })
          : '';
        return `
          <tr>
            <td>${refundDate}</td>
            <td>${formatCurrency(refundAmount)}</td>
            <td>${payment.refundNote || ''}</td>
          </tr>`;
      }
      return '';
    }
    return payment.refunds.map(refund => {
      const rDate = refund.date
        ? new Date(refund.date).toLocaleDateString('es-ES', { timeZone: timezone, day: '2-digit', month: '2-digit', year: 'numeric' })
        : '';
      return `
        <tr>
          <td>${rDate}</td>
          <td>${formatCurrency(refund.amount)}</td>
          <td>${refund.note || ''}</td>
        </tr>`;
    }).join('');
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Recibo de Pago - ${patientName}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: "Helvetica Neue", Arial, sans-serif;
          margin: 12px;
          line-height: 1.35;
          color: #1a202c;
          background: #f7fafc;
        }

        .receipt {
          max-width: 640px;
          margin: 0 auto;
          background: #fff;
          border: 1px solid #e2e8f0;
          padding: 20px 24px;
          box-shadow: 0 2px 8px rgba(26, 32, 44, 0.06);
        }

        .receipt-header {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
          padding-bottom: 12px;
          border-bottom: 2px solid #2d3748;
          position: relative;
          min-height: 88px;
        }

        .logo-section {
          position: absolute;
          top: 5px;
          left: 0;
          width: 200px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          opacity: 0.8;
        }

        .logo-section img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          object-position: left center;
        }

        .header-content {
          width: 100%;
          text-align: center;
        }

        .clinic-name {
          font-size: 15px;
          font-weight: bold;
          text-transform: uppercase;
          color: #2d3748;
          margin-bottom: 4px;
        }

        .clinic-details {
          font-size: 10px;
          color: #4a5568;
        }

        .document-title {
          font-size: 13px;
          font-weight: bold;
          color: #2d3748;
          margin: 8px 0 4px 0;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .receipt-meta {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          color: #4a5568;
          margin: 10px 0;
        }

        .receipt-meta span {
          font-weight: 600;
          color: #2d3748;
        }

        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin: 12px 0;
          padding: 10px 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
        }

        .info-column h4 {
          font-size: 11px;
          font-weight: bold;
          color: #2d3748;
          margin-bottom: 5px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .info-item {
          margin: 3px 0;
          font-size: 11px;
        }

        .info-label {
          font-weight: 500;
          color: #4a5568;
        }

        .info-value {
          color: #2d3748;
        }

        .section-title {
          font-size: 12px;
          font-weight: bold;
          color: #2d3748;
          margin: 14px 0 6px 0;
          padding-bottom: 4px;
          border-bottom: 1px solid #e2e8f0;
          text-transform: uppercase;
        }

        .amounts-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
          margin: 8px 0;
        }

        .amounts-table th {
          background: #2d3748;
          color: #fff;
          padding: 7px 10px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
        }

        .amounts-table th.text-right,
        .amounts-table td.text-right {
          text-align: right;
        }

        .amounts-table td {
          padding: 7px 10px;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: top;
        }

        .amounts-table tr:last-child td {
          border-bottom: none;
        }

        .amounts-table .total-row td {
          font-weight: bold;
          background: #f8fafc;
          border-top: 2px solid #2d3748;
          font-size: 13px;
        }

        .refunded-badge {
          display: inline-block;
          background: #fed7d7;
          color: #c53030;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: bold;
        }

        .refund-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          margin: 6px 0;
        }

        .refund-table th {
          background: #718096;
          color: #fff;
          padding: 5px 8px;
          text-align: left;
          font-size: 10px;
          font-weight: 600;
        }

        .refund-table td {
          padding: 5px 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .note-box {
          background: #fffbeb;
          border: 1px solid #f6e05e;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 11px;
          color: #744210;
          margin: 8px 0;
        }

        .note-icon {
          font-size: 11px;
          margin-right: 4px;
        }

        .signature-section {
          margin-top: 32px;
          padding-top: 12px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
        }

        .signature-block {
          text-align: center;
          width: 180px;
        }

        .signature-line {
          border-bottom: 1px solid #2d3748;
          height: 40px;
          margin-bottom: 4px;
        }

        .signature-label {
          font-size: 10px;
          color: #4a5568;
        }

        .footer {
          margin-top: 16px;
          padding-top: 10px;
          border-top: 1px solid #e2e8f0;
        }

        .print-date {
          font-size: 11px;
          color: #718096;
        }

        @media print {
          body {
            background: #fff;
            margin: 0;
          }
          .receipt {
            box-shadow: none;
            border: none;
            max-width: 100%;
            padding: 10px;
          }
        }
      </style>
    </head>
    <body>
      <div class="receipt">
        <div class="receipt-header">
          ${logoUrl ? `<div class="logo-section"><img src="${logoUrl}" alt="Logo" /></div>` : ''}
          <div class="header-content">
            <div class="clinic-name">${clinicName}</div>
            ${clinicAddress ? `<div class="clinic-details">${clinicAddress}</div>` : ''}
            ${clinicPhone ? `<div class="clinic-details">${clinicPhone}</div>` : ''}
            <div class="document-title">Recibo de Pago</div>
          </div>
        </div>

        <div class="info-grid">
          <div class="info-column">
            <h4>Paciente</h4>
            <div class="info-item"><span class="info-label">Nombre: </span><span class="info-value">${patientName}</span></div>
            ${patientDob ? `<div class="info-item"><span class="info-label">F. Nacimiento: </span><span class="info-value">${patientDob}</span></div>` : ''}
            ${patient?.email ? `<div class="info-item"><span class="info-label">Correo: </span><span class="info-value">${patient.email}</span></div>` : ''}
            ${patient?.phone ? `<div class="info-item"><span class="info-label">Teléfono: </span><span class="info-value">${patient.phone}</span></div>` : ''}
          </div>
          <div class="info-column">
            <h4>Información del Pago</h4>
            <div class="info-item"><span class="info-label">Fecha de pago: </span><span class="info-value">${paymentDate}</span></div>
            <div class="info-item"><span class="info-label">Forma de pago: </span><span class="info-value">${paymentFormName}</span></div>
            ${allocationLabel ? `<div class="info-item"><span class="info-label">Asignado a: </span><span class="info-value">${allocationLabel}</span></div>` : ''}
          </div>
        </div>

        <div class="section-title">Detalle del Pago</div>
        <table class="amounts-table">
          <thead>
            <tr>
              <th>Concepto</th>
              <th class="text-right">Monto</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${paymentConcept}</td>
              <td class="text-right">${formatCurrency(paymentAmount)}</td>
            </tr>
            ${hasRefunds ? `
            <tr>
              <td>Reembolsos${isFullyRefunded ? ' <span class="refunded-badge">Reembolso Completo</span>' : ''}</td>
              <td class="text-right">-${formatCurrency(refundAmount)}</td>
            </tr>
            ` : ''}
            <tr class="total-row">
              <td>Total Neto</td>
              <td class="text-right">${formatCurrency(netAmount)}</td>
            </tr>
          </tbody>
        </table>

        ${payment.note ? `
        <div class="note-box">
          <span class="note-icon">📝</span> ${payment.note}
        </div>
        ` : ''}

        ${hasRefunds && (payment.refunds?.length > 0 || payment.refundNote) ? `
        <div class="section-title">Historial de Reembolsos</div>
        <table class="refund-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Monto</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            ${refundsHistoryRows()}
          </tbody>
        </table>
        ` : ''}

        <div class="signature-section">
          <div class="signature-block">
            <div class="signature-line"></div>
            <div class="signature-label">Firma del Paciente</div>
            <div class="signature-label">${patientName}</div>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <div class="signature-label">${doctorName ? `${doctorTitle}` : 'Responsable'}</div>
            ${doctorName ? `<div class="signature-label">${doctorName}</div>` : ''}
            ${doctorLicense ? `<div class="signature-label">Cédula: ${doctorLicense}</div>` : ''}
          </div>
        </div>

        <div class="footer">
          <div class="print-date">Impreso el ${currentDate}</div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Opens a print window with the payment receipt
 * @param {Object} payment - Payment data
 * @param {Object} patient - Patient data
 * @param {string} logoUrl - Organization logo URL (optional)
 * @param {Object} organization - Organization data (optional)
 * @param {Object} doctor - Doctor/profile data (optional)
 * @param {Array} paymentForms - List of payment forms (optional)
 * @param {Array} users - List of users for allocation label (optional)
 */
export const printPaymentReceipt = (
  payment,
  patient,
  logoUrl = null,
  organization = null,
  doctor = null,
  paymentForms = [],
  users = []
) => {
  try {
    const printContent = generatePaymentReceiptHTML(payment, patient, logoUrl, organization, doctor, paymentForms, users);

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('No se pudo abrir la ventana de impresión. Verifica que no esté bloqueada por el navegador.');
    }

    printWindow.document.write(printContent);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  } catch (error) {
    console.error('Error printing payment receipt:', error);
    throw new Error('Error al imprimir el recibo: ' + error.message);
  }
};
